/**
 * The Dice tab of the cosmetics store: a grid of dice sets, each tile the
 * card the skin was cut from as a stylised backdrop with a still of the
 * set's six dice standing on it. The still is baked from the same rig that
 * runs the close-up (`bakeShelfPreview`), so browsing costs nothing: two
 * images per tile, no WebGL, no atlases, until a set is opened.
 *
 * Click a tile and it flies to the centre, enlarged; there the live rig
 * mounts inside the frame and fades in over the still once its six dice
 * stand, and the dice can be hovered and turned. Backdrop click or Escape
 * fades the close-up away, live dice and all. Purchase goes through
 * the shop's rolling basket like portrait and background packs: a button on
 * hover adds the set, the bar at the foot of the view takes the sale.
 */
import { Component, createRef } from 'preact';
import { motion, AnimatePresence } from 'framer-motion';
import { CoinIcon } from './ui/icons';
import { GOLD, TEXT_PRIMARY, TEXT_BODY, RADIUS, innerRadius } from '../lib/medievalTheme';
import { playUI, UI } from '../utils/arena/uiSounds';
import { DICE_SKINS } from '../utils/game/dice3d/diceSkins.data';
import { DiceSetRig, shelfLayout } from '../utils/game/dice3d/diceSetRig';
import { skinDieAssetsAsync } from '../utils/game/dice3d/dieAssets';
import { DiceSetPreview, DiceSetPreviewCaption, getDiceSkinSwatch } from './cosmeticPackPreview';
import { pushDismiss, removeDismiss } from '../utils/escStack';
import { t } from '../localization/t';

const PLATE_BG = `url("/tex-noise.webp"), url("/tex-stone.webp"), linear-gradient(180deg, rgba(20,15,9,0.96) 0%, rgba(10,7,4,0.96) 100%)`;
/** The label shadow the invite and replay cards write with, so text sits on art the same way here. */
const LABEL_SHADOW = '0 1px 2px rgba(0,0,0,0.95), 0 0 6px rgba(0,0,0,0.85), 0 0 14px rgba(0,0,0,0.6)';
/** How long the tile takes to fly to the centre. */
const FOCUS_MS = 520;
const FOCUS_EASE = 'cubic-bezier(0.2, 0.8, 0.2, 1)';
/** The close: backdrop and close-up fade, the close-up easing down a touch. */
const CLOSE = { duration: 0.28, ease: 'easeInOut' };
/** How long the live dice take to fade in over the still, and out again. */
const LIVE_MS = 260;
/**
 * The frame's corner, the gutter the foot keeps from it on every side, and
 * what that leaves the button: xl minus the panel gutter lands on md, the
 * ramp's step for a control (see `--radius-*` in index.css). The frame's
 * shared replay strokes sit outside the plate, leaving the full gutter.
 */
const FRAME_RADIUS = RADIUS.xl;
const FOOT_GAP = '0.375cqw';
const BUTTON_RADIUS = innerRadius(FRAME_RADIUS, FOOT_GAP);
/** Atlas scale the close-up cuts at; matches the rig's. */
const ATLAS_SCALE = 1;

export default class DiceSkinShelf extends Component {
  tiles = new Map();
  /** The enlarged copy's frame and canvas, and the grid frame it flew out of. */
  focusEl = null;
  liveCanvas = createRef();
  focusFrom = null;
  rig = null;
  frame = 0;
  last = null;
  observer = null;
  drag = null;
  escToken = null;
  state = { hovered: null, focused: null, live: false };

  componentDidUpdate(prev, prevState) {
    // The rig is disposed when the close-up's exit animation ends, not
    // here, so the live dice fade out with the frame instead of vanishing.
    if (this.state.focused && prevState.focused !== this.state.focused) {
      this.flyIn();
      this.mountRig(this.props.packs.find((p) => p.id === this.state.focused));
    }
  }

  componentWillUnmount() {
    if (this.escToken !== null) removeDismiss(this.escToken);
    this.unmountRig();
  }

  /** The live rig inside the close-up's frame; fades in once all six dice stand. */
  mountRig(pack) {
    this.unmountRig();
    const canvas = this.liveCanvas.current;
    const skin = pack && DICE_SKINS[pack.diceSkinId];
    if (!canvas || !skin) return;
    try {
      this.rig = new DiceSetRig(canvas, skin);
    } catch (err) {
      // No WebGL or out of contexts: the still stays, the set just does not come alive.
      console.error('[dice-store] live dice unavailable', err);
      return;
    }
    const rig = this.rig;
    this.observer = new ResizeObserver(this.measure);
    this.observer.observe(canvas);
    this.measure();
    rig.ready.then(() => {
      if (this.rig !== rig) return;
      rig.tick(0);
      this.setState({ live: true });
    });
    const loop = (t) => {
      const dt = this.last === null ? 0 : Math.min(0.05, (t - this.last) / 1000);
      this.last = t;
      rig.tick(dt);
      // How many dice stand, for the GUI spec's clock.
      const count = String(rig.dice.length);
      if (canvas.dataset.dice !== count) canvas.dataset.dice = count;
      this.frame = requestAnimationFrame(loop);
    };
    this.frame = requestAnimationFrame(loop);
  }

  unmountRig = () => {
    cancelAnimationFrame(this.frame);
    this.last = null;
    this.observer?.disconnect();
    this.observer = null;
    this.unbind();
    this.rig?.dispose();
    this.rig = null;
  };

  measure = () => {
    const canvas = this.liveCanvas.current;
    if (!canvas || !this.rig) return;
    const r = canvas.getBoundingClientRect();
    if (r.width < 1 || r.height < 1) return;
    this.rig.setSize(Math.round(r.width), Math.round(r.height), Math.min(2, window.devicePixelRatio || 1));
  };

  setTile = (id, el) => {
    if (el) this.tiles.set(id, el);
    else this.tiles.delete(id);
  };

  /** Hovering a tile starts its atlases cutting, so the close-up's dice are usually waiting. */
  warm(pack) {
    const skin = DICE_SKINS[pack.diceSkinId];
    if (!skin) return;
    for (const { sides } of shelfLayout(1, 1)) skinDieAssetsAsync(sides, ATLAS_SCALE, skin);
  }

  /** A click on a tile brings that set to the centre, enlarged and alive. */
  focus(pack) {
    if (this.state.focused) return;
    this.focusFrom = this.tiles.get(pack.id)?.getBoundingClientRect() || null;
    this.escToken = pushDismiss(this.close);
    this.setState({ focused: pack.id, hovered: null, live: false });
    playUI(UI.CLICK);
  }

  /** The close-up fades away; AnimatePresence keeps it on screen for the exit. */
  close = () => {
    if (!this.state.focused) return;
    if (this.escToken !== null) removeDismiss(this.escToken);
    this.escToken = null;
    this.rig?.putDown();
    this.setState({ focused: null });
  };

  /** A click on the backdrop closes, unless it is the tail of a die drag let go out there. */
  onBackdropClick = () => {
    if (this.released) return;
    this.close();
  };

  /**
   * FLIP: the enlarged copy starts where the grid tile was, at the grid
   * tile's size, and eases to its resting place in the centre.
   */
  flyIn() {
    const el = this.focusEl?.parentElement;
    if (!el) return;
    el.style.transition = 'none';
    el.style.transform = this.slotTransform();
    void el.offsetWidth;
    el.style.transition = `transform ${FOCUS_MS}ms ${FOCUS_EASE}`;
    el.style.transform = 'none';
  }

  /** The transform that puts the enlarged copy exactly over its grid slot. */
  slotTransform() {
    const from = this.focusFrom;
    const to = this.focusEl?.parentElement?.getBoundingClientRect();
    if (!from || !to) return 'none';
    return `translate(${from.left - to.left}px, ${from.top - to.top}px) scale(${from.width / to.width})`;
  }

  // Pointer handling on the close-up's frame only: the shelf's stills do not react.

  onMove = (e) => {
    if (this.drag || !this.rig) return;
    const hit = this.rig.hitTest(e.clientX, e.clientY);
    if (hit && hit !== this.rig.hovered) playUI(UI.HOVER, { volume: 0.4 });
    this.rig.setHover(hit);
  };

  onLeave = () => this.rig?.setHover(null);

  onDown = (e) => {
    if (e.button !== 0) return;
    const die = this.rig?.hitTest(e.clientX, e.clientY);
    if (!die) return;
    e.preventDefault();
    e.stopPropagation();
    this.drag = { x: e.clientX, y: e.clientY, t: performance.now() };
    this.rig.setHover(null);
    this.rig.hold(die);
    playUI(UI.UNEQUIP);
    this.bind();
  };

  onWindowMove = (e) => {
    const d = this.drag;
    if (!d) return;
    const now = performance.now();
    this.rig?.drag(e.clientX - d.x, e.clientY - d.y, (now - d.t) / 1000);
    d.x = e.clientX;
    d.y = e.clientY;
    d.t = now;
  };

  onWindowUp = () => {
    this.unbind();
    this.rig?.putDown();
    // The release lands a click on whatever is under the pointer, the frame
    // or the backdrop; both ignore it while this flag is up.
    this.released = true;
    setTimeout(() => { this.released = false; }, 0);
    this.drag = null;
  };

  onClickCapture = (e) => {
    if (this.released) {
      e.stopPropagation();
      e.preventDefault();
    }
  };

  bind() {
    window.addEventListener('mousemove', this.onWindowMove);
    window.addEventListener('mouseup', this.onWindowUp);
  }

  unbind() {
    window.removeEventListener('mousemove', this.onWindowMove);
    window.removeEventListener('mouseup', this.onWindowUp);
  }

  renderBasketButton(pack, enlarged) {
    const { buying, cart, onAdd, onRemove } = this.props;
    const inBasket = (cart[pack.id] || 0) > 0;
    const cost = pack.price?.coins || 0;
    const shown = enlarged || this.state.hovered === pack.id || inBasket;
    return (
      <button
        type="button"
        className={`vh-invite-btn ${inBasket ? '' : 'is-primary'} inline-flex items-center gap-1.5 whitespace-nowrap shrink-0`}
        style={{
          borderRadius: enlarged ? RADIUS.md : BUTTON_RADIUS,
          ...(enlarged ? { fontSize: '0.875cqw', padding: '0.4cqw 1.1cqw' } : {}),
          opacity: shown ? 1 : 0,
          transform: shown ? 'none' : 'translateY(0.3cqw)',
          transition: 'opacity 0.18s ease-out, transform 0.18s ease-out',
        }}
        disabled={buying}
        title={inBasket ? 'Take it back out of the basket' : `Add to the basket for ${cost} gold`}
        onClick={(e) => { e.stopPropagation(); (inBasket ? onRemove : onAdd)(pack); }}
      >
        <span>{inBasket ? 'In basket' : 'Add to basket'}</span>
        {inBasket ? null : (
          <>
            <span style={{ opacity: 0.75 }}>&middot;</span>
            <span className="inline-flex items-center gap-0.5 tabular-nums">
              {cost}
              <CoinIcon size={11} />
            </span>
          </>
        )}
      </button>
    );
  }

  /**
   * The art frame: the source card as a backdrop, cover-cropped to the band
   * where a Sorcery card keeps its illustration, darkened toward the foot
   * and lit from below in the skin's colour, with the set's still on top.
   * `enlarged` is the close-up's copy: it carries the live canvas too, and
   * its foot moves out below the frame, so the picture is the whole frame.
   */
  renderFrame(pack, enlarged) {
    const { owned } = this.props;
    const { hovered, focused, live } = this.state;
    const ownsThis = pack.cosmeticId ? owned.has(pack.cosmeticId) : false;
    const isHovered = enlarged || (hovered === pack.id && !focused);
    const swatch = getDiceSkinSwatch(pack) || '#3a2a18';

    return (
      <div
        ref={enlarged ? (el) => { this.focusEl = el; } : (el) => this.setTile(pack.id, el)}
        data-dice-tile={enlarged ? undefined : pack.id}
        className="vh-mode-tile"
        style={{
          aspectRatio: '16 / 10',
          background: swatch,
          // The live close-up keeps its geometry steady while dice are dragged.
          // The replay frame still owns every rim, stroke and hover shadow.
          ...(enlarged ? { transform: 'none' } : {}),
        }}
        onMouseMove={enlarged ? this.onMove : undefined}
        onMouseLeave={enlarged ? this.onLeave : undefined}
        onMouseDown={enlarged ? this.onDown : undefined}
        onClickCapture={enlarged ? this.onClickCapture : undefined}
      >
        <DiceSetPreview pack={pack} hovered={isHovered} stillHidden={enlarged && live} />
        {enlarged ? (
          <canvas
            ref={this.liveCanvas}
            data-dice-live
            className="absolute inset-0 block w-full h-full"
            style={{ opacity: live ? 1 : 0, transition: `opacity ${LIVE_MS}ms ease-out` }}
          />
        ) : null}
        {ownsThis ? (
          <div
            className="absolute top-2 left-2 px-2 py-0.5 text-[0.55cqw] font-bold"
            style={{
              color: '#8fe07a',
              background: PLATE_BG,
              backgroundBlendMode: 'overlay, normal, normal',
              border: `1px solid ${GOLD} 0.5)`,
              borderRadius: 'var(--radius-sm)',
              boxShadow: 'inset 0 1px 0 rgba(255,220,140,0.2), 0 2px 6px rgba(0,0,0,0.6)',
              textShadow: '0 1px 2px rgba(0,0,0,0.9)',
              zIndex: 4,
            }}
          >
            Owned
          </div>
        ) : null}
        {enlarged ? null : (
          <DiceSetPreviewCaption pack={pack}>
            {ownsThis ? null : this.renderBasketButton(pack, false)}
          </DiceSetPreviewCaption>
        )}
      </div>
    );
  }

  renderTile(pack) {
    return (
      <div
        key={pack.id}
        className="relative"
        onMouseEnter={() => { this.setState({ hovered: pack.id }); this.warm(pack); }}
        onMouseLeave={() => this.setState((s) => (s.hovered === pack.id ? { hovered: null } : null))}
        onClick={() => this.focus(pack)}
      >
        {this.renderFrame(pack, false)}
      </div>
    );
  }

  /** The close-up: the frame, and beneath it name, description and the basket. */
  renderFocused(pack) {
    const { owned } = this.props;
    const ownsThis = pack.cosmeticId ? owned.has(pack.cosmeticId) : false;
    return (
      <div
        className="relative flex flex-col items-center"
        style={{ width: '56cqw', transformOrigin: 'top left' }}
        onClick={(e) => e.stopPropagation()}
      >
        {this.renderFrame(pack, true)}
        <div className="flex flex-col items-center gap-2 mt-4 text-center">
          <div className="arena-heading text-[1.3cqw]" style={{ color: TEXT_PRIMARY, textShadow: LABEL_SHADOW }}>{pack.name}</div>
          {pack.description ? (
            <div className="text-[0.75cqw]" style={{ color: TEXT_BODY, textShadow: LABEL_SHADOW }}>{t(pack.description)}</div>
          ) : null}
          {ownsThis ? (
            <div className="text-[0.75cqw]" style={{ color: TEXT_BODY, textShadow: LABEL_SHADOW }}>{t('Yours. Equip it from Your dice, beside the colour swatches in the dice tray.')}</div>
          ) : (
            <div className="mt-1">{this.renderBasketButton(pack, true)}</div>
          )}
        </div>
      </div>
    );
  }

  render() {
    const { packs } = this.props;
    const { focused } = this.state;
    const focusedPack = focused ? packs.find((p) => p.id === focused) : null;
    return (
      <div className="relative flex-1 min-h-0">
        <div className="h-full overflow-y-auto px-6 py-4">
          {/* Fixed columns, centred: a shelf with two sets on it should not
              stretch them to fill the row, nor leave them hugging the left. */}
          <div
            className="grid gap-x-5 gap-y-6 justify-center mx-auto"
            style={{ maxWidth: '84cqw', gridTemplateColumns: 'repeat(auto-fill, 27cqw)' }}
          >
            {packs.map((pack) => this.renderTile(pack))}
          </div>
        </div>
        {/* The close-up: a lightly dimmed, blurred store behind the chosen
            set enlarged in the middle. A click anywhere off it, or Escape,
            fades it away; the rig lives until that fade has ended. */}
        <AnimatePresence onExitComplete={this.unmountRig}>
          {focusedPack ? (
            <motion.div
              key={focusedPack.id}
              className="fixed inset-0 flex items-center justify-center"
              style={{ zIndex: 60, background: 'rgba(0,0,0,0.42)', backdropFilter: 'blur(6px)' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={CLOSE}
              onClick={this.onBackdropClick}
            >
              <motion.div exit={{ opacity: 0, scale: 0.96 }} transition={CLOSE}>
                {this.renderFocused(focusedPack)}
              </motion.div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    );
  }
}
